// src/pages/orders/components/OrderSummarySection.jsx
import React from 'react';
import {
  DollarSign,
  Receipt,
  CreditCard,
  Truck,
  Loader2,
  Lock,
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { calculatePaymentBalance, formatCurrency } from '../../../utils/orderCalculations';
import DeliveryChargeWeightSection from './shared/DeliveryChargeWeightSection';
import styles from './OrderSummarySection.module.css';

const OrderSummarySection = ({
  orderTotals,
  paymentRecords,
  isCalculatingDelivery,
  canChangeDeliveryCharge,
  deliveryCharge,
  onDeliveryChargeChange,
  isStorePickup = false,
  weight,
  onWeightChange,
  hidePaymentSummary = false,
  deliveryChargeError = ''
}) => {
  const { t } = useLocalization();

  const paymentBalance = calculatePaymentBalance(orderTotals.grandTotal, paymentRecords);

  const formatNumberForDisplay = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toString();
  };

  const handleDeliveryChargeChange = (value) => {
    if (value === '' || value === null || value === undefined) {
      onDeliveryChargeChange(0);
      return;
    }
    // Negative delivery charges are not allowed — drop any minus sign
    const stringValue = value.toString().replace(/-/g, '');
    if (stringValue === '') {
      onDeliveryChargeChange(0);
      return;
    }
    if (stringValue.endsWith('.') || stringValue.endsWith('.0')) return;
    const numValue = parseFloat(stringValue);
    if (!isNaN(numValue)) {
      onDeliveryChargeChange(Math.max(0, numValue));
    }
  };

  return (
    <div className={styles.orderSummaryContent}>
      <div className={styles.summaryItems}>
        <DeliveryChargeWeightSection
          weight={weight}
          onWeightChange={onWeightChange}
          deliveryCharge={deliveryCharge}
          onDeliveryChargeChange={onDeliveryChargeChange}
          isCalculatingDelivery={isCalculatingDelivery}
          canChangeDeliveryCharge={canChangeDeliveryCharge}
          hideDeliveryChargeRow
        />

        {/* Subtotal */}
        <div className={styles.summaryRow}>
          <span className={styles.rowLabel}>
            <Receipt size={13} className={styles.rowIcon} />
            {t('ecomOrders.subtotal')}
          </span>
          <span className={styles.rowValue}>{formatCurrency(orderTotals.totalAmount)}</span>
        </div>

        {/* Discount */}
        {orderTotals.totalDiscount > 0 && (
          <div className={`${styles.summaryRow} ${styles.discountRow}`}>
            <span className={styles.rowLabel}>
              <Receipt size={13} className={styles.rowIcon} />
              {t('ecomOrders.totalDiscount')}
            </span>
            <span className={styles.rowValueDiscount}>-{formatCurrency(orderTotals.totalDiscount)}</span>
          </div>
        )}

        {/* Delivery Charge */}
        <div className={styles.summaryRow}>
          <span className={styles.rowLabel}>
            <Truck size={13} className={styles.rowIcon} />
            {t('ecomOrders.deliveryCharge')}
            {isCalculatingDelivery && <Loader2 size={12} className={styles.calcIcon} />}
          </span>
          <span className={styles.rowValue}>
            {canChangeDeliveryCharge && !isCalculatingDelivery && !isStorePickup ? (
              <div className={styles.editableDelivery}>
                <DollarSign size={12} className={styles.currencyIcon} />
                <input
                  type="text"
                  value={formatNumberForDisplay(deliveryCharge)}
                  onChange={(e) => handleDeliveryChargeChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                  className={styles.deliveryInput}
                />
              </div>
            ) : (
              <span className={styles.deliveryValue}>
                {formatCurrency(orderTotals.deliveryCharge)}
                {(!canChangeDeliveryCharge || isStorePickup) && <Lock size={11} className={styles.lockIcon} />}
              </span>
            )}
          </span>
        </div>
        {deliveryChargeError && <span className={styles.errorText}>{deliveryChargeError}</span>}

        {/* Tax */}
        {orderTotals.totalTax > 0 && (
          <div className={styles.summaryRow}>
            <span className={styles.rowLabel}>
              <Receipt size={13} className={styles.rowIcon} />
              {t('ecomOrders.tax')}
            </span>
            <span className={styles.rowValue}>{formatCurrency(orderTotals.totalTax)}</span>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      {/* Grand Total */}
      <div className={styles.grandTotal}>
        <span className={styles.grandTotalLabel}>
          <DollarSign size={16} />
          {t('ecomOrders.grandTotal')}
        </span>
        <span className={styles.grandTotalValue}>
          {formatCurrency(orderTotals.grandTotal)}
        </span>
      </div>

      {/* Payment Summary - only shown when not hidden and records exist */}
      {!hidePaymentSummary && paymentRecords.length > 0 && (
        <>
          <div className={styles.divider} />
          <div className={styles.paymentSummary}>
            <h4 className={styles.paymentSummaryTitle}>
              <CreditCard size={14} />
              Payment Summary
            </h4>
            
            <div className={styles.paymentRow}>
              <span>{t('ecomOrders.totalPaid')}</span>
              <span className={styles.paymentRowValue}>{formatCurrency(paymentBalance.totalPaid)}</span>
            </div>
            
            <div className={`${styles.paymentRow} ${styles.balanceRow}`}>
              <span>
                {paymentBalance.isOverpaid ? t('ecomOrders.overpaidAmount') : t('ecomOrders.paymentBalance')}
              </span>
              <span className={`${styles.paymentRowValue} ${
                paymentBalance.isFullyPaid 
                  ? paymentBalance.isOverpaid ? styles.overpaid : styles.paid
                  : styles.pending
              }`}>
                {formatCurrency(paymentBalance.balance)}
              </span>
            </div>

            <div className={styles.statusBadgeWrap}>
              <span className={`${styles.statusBadge} ${
                paymentBalance.isFullyPaid 
                  ? paymentBalance.isOverpaid ? styles.overpaidBadge : styles.paidBadge
                  : styles.pendingBadge
              }`}>
                {paymentBalance.isFullyPaid 
                  ? paymentBalance.isOverpaid ? 'Overpaid' : 'Fully Paid'
                  : 'Payment Pending'
                }
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderSummarySection;